import { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, setDoc, deleteDoc, updateDoc, Timestamp } from "firebase/firestore";

// Go-live: only show orphans created at/after this moment. Frozen value.
const GO_LIVE_CUTOFF_MS = 1783411502976; // 2026-07-07 08:05:02 UTC

interface OrphanSubmission {
  id: string;
  userId: string;
  taskId: string;
  taskTitle: string;
  taskType: string;
  reward: number;
  mediaUrl: string;
  createdAtMs: number;
  videoMetadata?: { duration?: number };
}

function isOrphan(
  entries: unknown[],
  data: Record<string, unknown>,
  docId: string
): boolean {
  if (!entries.length) return true;
  const submissionId = (data.submissionId as string) || docId;
  const mediaUrl = data.mediaUrl as string | undefined;
  return !entries.some((entry) => {
    if (!entry || typeof entry !== "object") return false;
    const e = entry as Record<string, unknown>;
    return (
      e.submissionId === submissionId ||
      e.submissionId === docId ||
      e.id === docId ||
      (e.mediaUrl && mediaUrl && e.mediaUrl === mediaUrl)
    );
  });
}

export default function OrphanSubmissions() {
  const [activeTab, setActiveTab] = useState<"photo" | "video">("photo");
  const [loading, setLoading] = useState(true);
  const [orphans, setOrphans] = useState<OrphanSubmission[]>([]);

  useEffect(() => {
    let cancelled = false;

    const scan = async () => {
      setLoading(true);
      const taskType = activeTab === "photo" ? "Photo" : "Video";
      const collected: OrphanSubmission[] = [];

      try {
        const q = query(
          collection(db, "submissions"),
          where("taskType", "==", taskType),
          orderBy("createdAt", "desc"),
          limit(200)
        );
        const snapshot = await getDocs(q);

        for (const submissionDoc of snapshot.docs) {
          const data = submissionDoc.data();
          const createdAtMs =
            data.createdAt &&
            typeof data.createdAt === "object" &&
            "toMillis" in data.createdAt
              ? (data.createdAt as { toMillis: () => number }).toMillis()
              : typeof data.createdAt === "number"
                ? data.createdAt
                : 0;

          if (createdAtMs < GO_LIVE_CUTOFF_MS) continue;

          const userId = data.userId as string | undefined;
          if (!userId) continue;

          try {
            const userSnap = await getDoc(doc(db, "users", userId));
            let orphan = false;

            if (!userSnap.exists()) {
              orphan = true;
            } else {
              const userData = userSnap.data();
              const taskData = userData.completedTasks?.[data.taskId as string];
              const submissions = Array.isArray(taskData?.submissions)
                ? taskData.submissions
                : [];
              orphan = isOrphan(submissions, data, submissionDoc.id);
            }

            if (orphan) {
              collected.push({
                id: submissionDoc.id,
                userId,
                taskId: (data.taskId as string) || "",
                taskTitle: (data.taskTitle as string) || "",
                taskType: (data.taskType as string) || taskType,
                reward: Number(data.reward) || 0,
                mediaUrl: (data.mediaUrl as string) || "",
                createdAtMs,
                videoMetadata: data.videoMetadata as OrphanSubmission["videoMetadata"],
              });
            }
          } catch (err) {
            console.error(
              `Orphan scan: failed user read for submission ${submissionDoc.id}`,
              err
            );
          }
        }
      } catch (err) {
        console.error("Orphan scan failed:", err);
      }

      if (!cancelled) {
        setOrphans(collected);
        setLoading(false);
      }
    };

    scan();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const photoCount = activeTab === "photo" ? orphans.length : 0;
  const videoCount = activeTab === "video" ? orphans.length : 0;

  async function handleDelete(orphan: OrphanSubmission) {
    const ok = window.confirm(
      "Permanently DELETE this orphan submission?\n\nTask: " + (orphan.taskTitle || orphan.taskId) +
      "\nUser: " + orphan.userId +
      "\n\nThis removes the submission record. Use this when the user successfully re-uploaded a different good one. This cannot be undone."
    );
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "submissions", orphan.id));
      setOrphans(prev => prev.filter(o => o.id !== orphan.id));
      window.alert("Deleted.");
    } catch (err) {
      console.error("Delete failed:", err);
      window.alert("Delete failed. See console.");
    }
  }

  async function handleRepair(orphan: OrphanSubmission) {
    const ok = window.confirm(
      "REPAIR this submission?\n\nTask: " + (orphan.taskTitle || orphan.taskId) +
      "\nUser: " + orphan.userId +
      "\n\nThis re-creates the user's record as a fresh PENDING submission and sends it back to the normal Approvals queue. No reward is given here — you approve or reject it in Approvals as usual."
    );
    if (!ok) return;
    try {
      const subSnap = await getDoc(doc(db, "submissions", orphan.id));
      if (!subSnap.exists()) { window.alert("Submission not found."); return; }
      const subData = subSnap.data() as Record<string, any>;
      const isVideo = subData.taskType === "Video";

      const entry: Record<string, any> = { ...subData };
      entry.submissionId = subData.submissionId || orphan.id;
      entry.status = "pending";
      delete entry.adminReviewedAt;
      delete entry.adminReviewedBy;
      delete entry.approvedAt;
      delete entry.rejectedAt;
      delete entry.rejectedBy;
      delete entry.rejectReason;
      delete entry.rewardAmount;
      if (isVideo) entry.videoStage = "approval";

      const userRef = doc(db, "users", orphan.userId);
      const userSnap = await getDoc(userRef);
      const userData: Record<string, any> = userSnap.exists() ? (userSnap.data() as Record<string, any>) : {};
      const completedTasks: Record<string, any> = userData.completedTasks || {};
      const existing = completedTasks[orphan.taskId];
      const existingSubs = (existing && Array.isArray(existing.submissions)) ? existing.submissions : [];

      const updatePayload = {
        completedTasks: {
          ...completedTasks,
          [orphan.taskId]: {
            ...(existing || {}),
            submissions: [...existingSubs, entry],
            lastSubmittedAt: entry.lastSubmittedAt || Timestamp.now(),
            taskTitle: entry.taskTitle || orphan.taskTitle || ""
          }
        }
      };
      await setDoc(userRef, updatePayload, { merge: true });

      const subReset: Record<string, any> = { status: "pending" };
      if (isVideo) subReset.videoStage = "approval";
      await updateDoc(doc(db, "submissions", orphan.id), subReset);

      setOrphans(prev => prev.filter(o => o.id !== orphan.id));
      window.alert("Repaired — now in Approvals as pending.");
    } catch (err) {
      console.error("Repair failed:", err);
      window.alert("Repair failed. See console.");
    }
  }

  return (
    <div>
      <div className="border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold mb-2">
          Orphan Submissions (needs repair)
        </h1>
        <p className="text-sm text-gray-500 mb-4">
          Submissions that reached the portal but were never saved to the user.
          Only showing items created after go-live.
        </p>

        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => setActiveTab("photo")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "photo"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Photo ({photoCount})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("video")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "video"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Video ({videoCount})
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-600">Scanning…</p>
      ) : orphans.length === 0 ? (
        <p className="text-gray-500">No orphan submissions.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr className="border-b text-left text-gray-600">
                <th className="py-2 pr-4">Preview</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2 pr-4">User ID</th>
                <th className="py-2 pr-4">Task</th>
                <th className="py-2 pr-4">Task ID</th>
                <th className="py-2 pr-4">Reward</th>
                {activeTab === "video" && (
                  <th className="py-2 pr-4">Duration</th>
                )}
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orphans.map((orphan) => (
                <tr key={orphan.id} className="border-b align-top">
                  <td className="py-3 pr-4">
                    {orphan.mediaUrl ? (
                      activeTab === "photo" ? (
                        <img
                          src={orphan.mediaUrl}
                          className="h-24 rounded object-cover"
                          alt=""
                        />
                      ) : (
                        <video
                          src={orphan.mediaUrl}
                          muted
                          playsInline
                          preload="metadata"
                          className="h-24 rounded object-cover"
                          style={{ pointerEvents: "none" }}
                        />
                      )
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap">
                    {new Date(orphan.createdAtMs).toLocaleString()}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs">{orphan.userId}</td>
                  <td className="py-3 pr-4">{orphan.taskTitle || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs">{orphan.taskId}</td>
                  <td className="py-3 pr-4">{orphan.reward}</td>
                  {activeTab === "video" && (
                    <td className="py-3 pr-4">
                      {orphan.videoMetadata?.duration != null
                        ? `${orphan.videoMetadata.duration}s`
                        : "—"}
                    </td>
                  )}
                  <td className="py-3 pr-4 whitespace-nowrap">
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 mr-2"
                      onClick={() => handleRepair(orphan)}
                    >
                      Repair
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                      onClick={() => handleDelete(orphan)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
